import { useState } from "react"
import * as ToggleGroup from "@radix-ui/react-toggle-group"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import Cookies from "js-cookie"

import {
  getAnnouncement,
  getLatestAnnouncement,
  validateAnnouncementPermission,
} from "@/lib/api/announcement"
import { canViewThisPage } from "@/lib/api/auth"
import { cn, formatDateAndTime } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from "@/components/ui/credenza"
import { AnnouncementForm } from "@/components/forms/announcement-form"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"

export const Route = createFileRoute("/_home/announcement/")({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_announcements")) {
      return
    }
    return redirect({ to: "/access-denied" })
  },
  component: AnnouncementPage,
})

function AnnouncementPage() {
  const { data: latestAnnouncement, isLoading } = useQuery({
    queryKey: ["latestAnnouncement"],
    queryFn: getLatestAnnouncement,
  })

  return (
    <main className="space-y-4 p-4">
      {/* 1) Title + Button on one row */}
      <div className="flex items-center justify-between">
        <Header>Announcements</Header>
        <Link to="/announcement/overall-announcement">
          <Button>
            <Icons.view />
            View Previous Announcements
          </Button>
        </Link>
      </div>

      {/* 2) Grid with stretch to equalize heights */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 items-stretch">
        {/* Left panel: form */}
        <div className="col-span-2 h-full">
          <AnnouncementForm />
        </div>

        {/* Right panel: latest announcement */}
        <Card className="w-full h-full flex flex-col">
          {/* Image */}
          <div className="w-full overflow-hidden">
            <img
              src="card-image-sampler.png"
              className="h-44 w-full object-cover"
            />
          </div>

          {/* Header */}
          <CardHeader className="text-lg font-bold">
            <CardTitle className="text-2xl font-medium">
              <div className="flex items-center justify-center gap-2">
                <Icons.partyPopper className="text-primary" />
                <Icons.partyPopper className="text-primary" />
                Latest Announcement
                <Icons.partyPopper className="text-primary" />
                <Icons.partyPopper className="text-primary" />
              </div>
            </CardTitle>
            <CardDescription className="flex flex-col py-2">
              <span className="py-3 text-3xl font-medium text-muted-foreground">
                {latestAnnouncement?.subject}
              </span>
              <span className="text-sm font-bold text-muted-foreground">
                {`${latestAnnouncement?.created_by} | ${formatDateAndTime(
                  latestAnnouncement?.created_at
                )}`}
              </span>
            </CardDescription>
          </CardHeader>

          {/* Content (flex-1 to grow) */}
          <CardContent className="flex-1 overflow-auto">
            <CardDescription>
              <span className="text-lg font-medium">
                {latestAnnouncement?.header}
              </span>
              <br />
              <br />
              {latestAnnouncement?.body}
            </CardDescription>
          </CardContent>

          {/* Footer with Credenza */}
          <CardFooter className="text-sm font-semibold">
            <Credenza>
              <CredenzaTrigger asChild>
                <span className="flex cursor-pointer px-0 text-primary underline">
                  Read more <Icons.doubleRight />
                </span>
              </CredenzaTrigger>
              <CredenzaContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                <CredenzaHeader>
                  <CredenzaTitle className="text-2xl">
                    <div className="flex items-center justify-center gap-2">
                      <Icons.partyPopper className="text-primary" />
                      <Icons.partyPopper className="text-primary" />
                      Latest Announcement
                      <Icons.partyPopper className="text-primary" />
                      <Icons.partyPopper className="text-primary" />
                    </div>
                  </CredenzaTitle>
                  <CredenzaDescription className="flex flex-col py-2">
                    <span className="text-xl font-medium">
                      {latestAnnouncement?.subject}
                    </span>
                    <span className="text-sm font-bold">
                      {`${latestAnnouncement?.created_by} | ${formatDateAndTime(
                        latestAnnouncement?.created_at
                      )}`}
                    </span>
                    <br />
                    <span className="text-lg font-medium">
                      {latestAnnouncement?.header}
                    </span>
                    <br />
                    <br />
                    {latestAnnouncement?.body}
                  </CredenzaDescription>
                </CredenzaHeader>
              </CredenzaContent>
            </Credenza>
          </CardFooter>
        </Card>
      </div>
    </main>
  )
}

export default AnnouncementPage
